import React from 'react';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import { useClaim } from '~/components/ClaimContainer';
import { getAllClaimRelatedRoles } from '~/components/communications/ContactUtils';
import ContactTextFieldFormik from '~/components/ContactTextFieldFormik';
import Button from '~/components/core/Atomic/Buttons/Button';
import Text from '~/components/core/TextComponents/Text';
import useOrganization from '~/components/OrganizationContext';
import TextFieldFormik from '~/components/TextFieldFormik';
import type {
  CreateSignatureRequestRequest,
  CreateSignatureRequestResponse,
} from '~/server_shared/openapi/data-contracts';
import type { ContactFullModel, DocumentMetaData } from '~/server_shared/swagger/data-contracts';
import { reportAxiosError } from '~/Utils';

import type { SignatureRequestData } from './utils';

interface Props {
  document: DocumentMetaData;
  onCancel: () => void;
  onSuccess: (data: SignatureRequestData) => void;
}

interface FormValues {
  contact_id: number | '';
  contact: ContactFullModel | null;
  document_id: number;
  title: string;
}

const PreSignatureRequestCard: React.FC<Props> = ({ document, onCancel, onSuccess }) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organizationId, organizationContactRolesDict } = useOrganization();
  const { claim } = useClaim({ claimRequired: true });

  const initialValues: FormValues = {
    contact_id: '',
    contact: null,
    document_id: document.id,
    title: document.document_name || '',
  };

  const validationSchema = Yup.object().shape({
    contact_id: Yup.number().required('Contact is required'),
    document_id: Yup.number().required('Document is required'),
    title: Yup.string().nullable().max(200, 'Title must be less than 200 characters'),
  });

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnBlur={false}
      onSubmit={async (values, formikProps) => {
        try {
          const requestData: CreateSignatureRequestRequest = {
            contact_id: values.contact_id as number,
            document_id: values.document_id,
            title: values.title,
            message: null,
          };

          const response = await axios.post<CreateSignatureRequestResponse>(
            `/api/v1/organizations/${organizationId}/e_sign/signature_requests`,
            requestData
          );

          onSuccess({
            signUrl: response.data.sign_url,
            contact: values.contact!,
            claimId: claim.id,
          });
        } catch (error) {
          formikProps.setSubmitting(false);
          reportAxiosError(error);
        }
      }}
    >
      {({ handleSubmit, isSubmitting, values }) => (
        <CardDialog isDialog title="Create Signature Request" fullWidth onClose={onCancel}>
          <div className="flex flex-col space-y-20">
            <div className="flex flex-col space-y-8">
              <div className="border-gray-300 bg-gray-50 flex gap-40 border pb-12">
                <div className="flex flex-col">
                  <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.MEDIUM} className="text-grey-400">
                    Document Type
                  </Text>
                  <Text variant={Text.VARIANTS.SM}>{document.type || '-'}</Text>
                </div>
                <div className="flex flex-col">
                  <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.MEDIUM} className="text-grey-400">
                    Document Name
                  </Text>
                  <Text variant={Text.VARIANTS.SM}>{document.document_name || '-'}</Text>
                </div>
              </div>
            </div>

            <ContactTextFieldFormik
              id="contact"
              label="Contact"
              fixedSearchResults
              fullWidth
              disabled={isSubmitting}
              acceptedRoles={getAllClaimRelatedRoles(organizationContactRolesDict)}
            />

            <TextFieldFormik
              id="title"
              label="Title"
              placeholder="Enter signature request title"
              fullWidth
              disabled={isSubmitting}
            />
          </div>

          <div className="border-gray-200 flex justify-end space-x-12 border-t p-20">
            <Button variant="text" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleSubmit()}
              disabled={isSubmitting || !values.contact_id || !values.document_id}
            >
              {isSubmitting ? 'Submitting...' : 'Get Signature Link'}
            </Button>
          </div>
        </CardDialog>
      )}
    </Formik>
  );
};

export default PreSignatureRequestCard;
